// Calendar math for the top-bar date picker (app-shell-redesign AC4). Pure — no
// DOM, no clock reads — so it unit-tests cleanly and the component stays a thin
// render layer over it.
import type { DatePickerMode } from './datePickerMode';

/** True when two dates fall on the same calendar day (ignores time). */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * A Monday-first calendar grid for the given month: an array of week rows, each
 * exactly 7 cells. Days sit at their weekday column; leading/trailing pad cells
 * are `null`.
 */
export function monthMatrix(year: number, month: number): (Date | null)[][] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // JS getDay() is Sunday=0; shift to Monday=0.
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;

  const cells: (Date | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Step a date by one unit in the picker's mode (day or month). Never mutates. */
export function stepDate(date: Date, mode: DatePickerMode, dir: 1 | -1): Date {
  const d = new Date(date.getTime());
  if (mode === 'year') {
    // Year mode only cares about the year — normalise to Jan 1 so the day/month
    // can't carry overflow into the step (and Feb 29 can't roll a leap step).
    d.setMonth(0, 1);
    d.setFullYear(d.getFullYear() + dir);
  } else if (mode === 'month') {
    // Normalise to day 1 BEFORE the month delta: setMonth keeps the day-of-month,
    // so stepping from a late-month day (e.g. Jan 31) would overflow (Feb 31 → Mar)
    // and skip a month. Month mode only cares about the month/year anyway.
    d.setDate(1);
    d.setMonth(d.getMonth() + dir);
  } else d.setDate(d.getDate() + dir);
  return d;
}
